import { useState, FC, useEffect } from "react";
import {
  CollectionDeleteContext,
  CollectionDeleteContextValue,
  CollectionDeleteProviderProps,
} from ".";
import { ErrorResponse } from "../../../Models/Error";
import { Collection } from "../../../Models/Collection";
import { CollectionsService } from "../../../services/shipment/collections";
import { callErrorToast } from "../../../utilities/utilities";

/* ===========================================================================
 * SERVICE INSTANTIATION
 * =========================================================================== */
const collectionsService = new CollectionsService();

/* ===========================================================================
 * PROVIDER: CollectionDeleteProvider
 * =========================================================================== */

/**
 * @component CollectionDeleteProvider
 *
 * Manages the state and logic for deleting multiple collections in batch.
 * It handles the execution flow of multiple delete requests, managing
 * concurrency via Promise.allSettled and providing global error feedback.
 *
 * @param {CollectionDeleteProviderProps} props - Provider properties including collection IDs and callback.
 * @returns {JSX.Element} A React Context Provider for collection deletion operations.
 */
export const CollectionDeleteProvider: FC<CollectionDeleteProviderProps> = ({
  children,
  data = [],
  callback,
}) => {
  /** Indicates if a batch deletion operation is currently processing. */
  const [loader, setLoader] = useState<boolean>(false);

  /** Stores the error response if one or more deletion requests fail. */
  const [error, setError] = useState<ErrorResponse<Collection> | null>(null);

  /* ---------------------------------------------------------------------------
   * FUNCTION: deleteCollections
   * Handles the asynchronous batch deletion process.
   * ------------------------------------------------------------------------- */

  /**
   * Triggers the deletion of all collection IDs provided in the data array.
   * Processes requests in parallel and handles partial failures.
   *
   * @async
   * @returns {Promise<void>}
   */
  const deleteCollections = async (): Promise<void> => {
    setLoader(true);

    try {
      // Execute all deletion requests concurrently
      const results = await Promise.allSettled(
        data.map((id) => collectionsService.delete(id))
      );

      // Separate results into successful and rejected categories
      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      const success = results.filter(
        (r): r is PromiseFulfilledResult<void> => r.status === "fulfilled"
      );

      // If at least one deletion failed
      if (failed.length > 0) {
        const firstError = failed[0].reason as ErrorResponse<Collection>;
        setError(firstError);

        // If some deletions succeeded, trigger the callback to refresh UI/state
        if (success.length > 0 && callback) {
          callback();
        }

        return;
      }

      // If all deletions were successful
      if (callback) callback();
    } catch (unexpected) {
      // Generic error fallback for unexpected exceptions
      const err: ErrorResponse<Collection> = {
        response: {
          data: {},
        },
      };

      setError(err);
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  /**
   * Monitors the error state to trigger a visual error notification.
   */
  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  /* ---------------------------------------------------------------------------
   * CONTEXT VALUE & RENDER
   * ------------------------------------------------------------------------- */

  const contextValue: CollectionDeleteContextValue = {
    data,
    loader,
    deleteCollections,
    callback,
  };

  return (
    <CollectionDeleteContext.Provider value={contextValue}>
      {children}
    </CollectionDeleteContext.Provider>
  );
};
